import { createTRPCClient, httpBatchLink, type CreateTRPCClient, type TRPCLink } from '@trpc/client';
import type { AnyTRPCRouter } from '@trpc/server';

/* Generic, router-agnostic factory for the typed tRPC client. Web and mobile */
/* call it with the server's `AppRouter` type (type-only import from apps/api), */
/* so this package stays free of any app dependency while the contract flows at */
/* the call site:  createApiClient<AppRouter>({ url, getAccessToken }). */

export interface ApiClientOptions {
  /** Backend tRPC endpoint, e.g. https://backend.naia-edu.app/trpc */
  readonly url: string;
  /** Supplies the Supabase JWT for the Authorization header (optional). */
  readonly getAccessToken?: () => string | null | Promise<string | null>;
  /** École active choisie dans le sélecteur de rôle (teacher/direction multi-école). */
  readonly getActiveSchoolId?: () => string | null;
}

/** Builds the request headers from the current access token / active school (if any). */
export async function buildAuthHeaders(
  getAccessToken?: ApiClientOptions['getAccessToken'],
  getActiveSchoolId?: ApiClientOptions['getActiveSchoolId'],
): Promise<Record<string, string>> {
  const token = await getAccessToken?.();
  const schoolId = getActiveSchoolId?.();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(schoolId ? { 'x-active-school-id': schoolId } : {}),
  };
}

export function createApiClient<TRouter extends AnyTRPCRouter>(
  options: ApiClientOptions,
): CreateTRPCClient<TRouter> {
  /* Cast: httpBatchLink over a generic router triggers the transformer-options */
  /* friction; our concrete routers carry no transformer, so a TRPCLink<TRouter> */
  /* cast is safe and keeps the factory router-agnostic. */
  const link = httpBatchLink<AnyTRPCRouter>({
    url: options.url,
    headers: () => buildAuthHeaders(options.getAccessToken, options.getActiveSchoolId),
  }) as unknown as TRPCLink<TRouter>;
  return createTRPCClient<TRouter>({ links: [link] });
}
